import { useState, useEffect } from 'react';
import PCComponent from '../../../components/base/PCComponent';
import GlitchText from '../../../components/base/GlitchText';

interface HeroSectionProps {
  onSectionChange?: (section: string) => void;
}

export default function HeroSection({ onSectionChange }: HeroSectionProps) {
  const [bootSequence, setBootSequence] = useState(0);
  const [showComponents, setShowComponents] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBootSequence(1);
      setTimeout(() => setShowComponents(true), 1000);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const pcComponents = [
    {
      icon: <i className="ri-cpu-line"></i>,
      title: "CPU CORE",
      description: "About Me",
      glowColor: "cyan",
      section: "about"
    },
    {
      icon: <i className="ri-dashboard-line"></i>,
      title: "GPU UNIT",
      description: "Skills & Tech",
      glowColor: "green",
      section: "skills"
    },
    {
      icon: <i className="ri-hard-drive-line"></i>,
      title: "SSD DRIVE",
      description: "Projects",
      glowColor: "orange",
      section: "projects"
    },
    {
      icon: <i className="ri-database-line"></i>,
      title: "MOTHERBOARD",
      description: "Portfolio",
      glowColor: "purple",
      section: "portfolio"
    },
    {
      icon: <i className="ri-wifi-line"></i>,
      title: "NETWORK",
      description: "Experience",
      glowColor: "blue",
      section: "experience"
    },
    {
      icon: <i className="ri-flashlight-line"></i>,
      title: "POWER BTN",
      description: "Contact",
      glowColor: "red",
      section: "contact"
    }
  ];

  const scrollToSection = (sectionId: string) => {
    try {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        onSectionChange?.(sectionId);
      }
    } catch (error) {
      console.error('Error scrolling to section:', error);
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
        <div 
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.03%22%3E%3Ccircle cx=%2230%22 cy=%2230%22 r=%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`
          }}
        ></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20">
        {/* Boot Sequence */}
        <div className={`text-center mb-16 transition-all duration-1000 ${bootSequence ? 'opacity-100' : 'opacity-0'}`}>
          <div className="mb-8">
            <h1 className="text-6xl md:text-8xl font-bold text-white font-orbitron mb-4">
              <GlitchText text="ADITYA.SYS" glitchIntensity="high" />
            </h1>
            <div className="text-xl md:text-2xl text-cyan-400 font-mono mb-2">
              PRODUCT_MANAGER.EXE INITIALIZED
            </div>
            <div className="text-lg text-gray-400 font-rajdhani">
              Building the future, one component at a time
            </div>
          </div>

          {/* System Status */}
          <div className="flex justify-center space-x-8 mb-8">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 font-mono text-sm">SYSTEM ONLINE</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
              <span className="text-cyan-400 font-mono text-sm">READY</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-orange-400 font-mono text-sm">BUILDING...</span>
            </div>
          </div>
        </div>

        {/* PC Building Workspace */}
        <div className={`transition-all duration-1000 ${showComponents ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white font-orbitron mb-4">
              INTERACTIVE WORKSPACE
            </h2>
            <p className="text-gray-400 font-rajdhani text-lg">
              Click on any component to explore my digital universe
            </p>
          </div>

          {/* Component Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pcComponents.map((component, index) => (
              <div
                key={component.section}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <PCComponent
                  icon={component.icon}
                  title={component.title}
                  description={component.description}
                  glowColor={component.glowColor}
                  onClick={() => scrollToSection(component.section)}
                />
              </div>
            ))}
          </div>

          {/* Holographic Robot Assistant */}
          <div className="mt-24 flex justify-center items-center">
            <div className="relative max-w-5xl w-full">
              
              {/* Holographic Display Screen */}
              <div className="relative mb-12">
                <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border-2 border-cyan-400/50 rounded-3xl p-8 backdrop-blur-md shadow-2xl shadow-cyan-400/20 max-w-3xl mx-auto">
                  {/* Screen Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-cyan-400/30">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                      <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                    <div className="text-cyan-400 font-mono text-sm">TRANSMISSION_ACTIVE</div>
                  </div>

                  {/* Message Content */}
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-400/50">
                        <i className="ri-robot-2-line text-2xl text-white"></i>
                      </div>
                      <div className="flex-1">
                        <div className="bg-gray-800/50 border border-cyan-400/30 rounded-2xl p-6">
                          <p className="text-xl md:text-2xl text-white font-rajdhani leading-relaxed">
                            Hey there! 👋 Want to know more about my journey and connect? 
                            Check out my <a href="https://x.com/Adi_03_02" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors underline font-semibold whitespace-nowrap">X profile</a> and 
                            <a href="https://www.linkedin.com/in/adityaprakash0302/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors underline font-semibold whitespace-nowrap"> LinkedIn</a> - 
                            let's build something amazing together! 🚀
                          </p>
                        </div>
                        <div className="mt-3 flex items-center space-x-2 text-gray-400 text-sm font-mono">
                          <i className="ri-time-line"></i>
                          <span>Just now</span>
                          <span className="mx-2">•</span>
                          <i className="ri-check-double-line text-cyan-400"></i>
                          <span className="text-cyan-400">Delivered</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Holographic Effects */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-400/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                  </div>
                </div>

                {/* Holographic Scan Lines */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/5 to-transparent animate-scan"></div>
                </div>
              </div>

              {/* Minimalist Robot Character */}
              <div className="flex justify-center">
                <div className="relative group">
                  {/* Floating Particles around Robot */}
                  <div className="absolute inset-0 -m-20">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-2 h-2 bg-cyan-400 rounded-full animate-float"
                        style={{
                          left: `${20 + Math.cos(i * Math.PI / 4) * 80}%`,
                          top: `${50 + Math.sin(i * Math.PI / 4) * 80}%`,
                          animationDelay: `${i * 0.3}s`,
                          animationDuration: '3s'
                        }}
                      ></div>
                    ))}
                  </div>

                  {/* Robot Body - Modern Geometric Design */}
                  <div className="relative transform hover:scale-105 transition-transform duration-500">
                    
                    {/* Energy Core (Center) */}
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full animate-pulse shadow-2xl shadow-cyan-400/50"></div>
                      <div className="absolute inset-2 bg-gradient-to-br from-gray-900 to-black rounded-full flex items-center justify-center">
                        <i className="ri-robot-2-fill text-5xl text-cyan-400 animate-pulse"></i>
                      </div>
                      {/* Orbiting Rings */}
                      <div className="absolute inset-0 border-4 border-cyan-400/30 rounded-full animate-spin-slow"></div>
                      <div className="absolute inset-4 border-2 border-purple-400/30 rounded-full animate-spin-reverse"></div>
                    </div>

                    {/* Holographic Base Platform */}
                    <div className="relative">
                      {/* Platform Layers */}
                      <div className="w-48 h-3 bg-gradient-to-r from-transparent via-cyan-400 to-transparent mx-auto mb-2 rounded-full shadow-lg shadow-cyan-400/50"></div>
                      <div className="w-40 h-2 bg-gradient-to-r from-transparent via-cyan-400/70 to-transparent mx-auto mb-2 rounded-full"></div>
                      <div className="w-32 h-1 bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent mx-auto rounded-full"></div>
                      
                      {/* Holographic Grid */}
                      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-64 h-32 pointer-events-none">
                        <div className="absolute inset-0 bg-gradient-to-t from-cyan-400/20 to-transparent" style={{
                          backgroundImage: `linear-gradient(0deg, transparent 24%, rgba(6, 182, 212, 0.1) 25%, rgba(6, 182, 212, 0.1) 26%, transparent 27%, transparent 74%, rgba(6, 182, 212, 0.1) 75%, rgba(6, 182, 212, 0.1) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(6, 182, 212, 0.1) 25%, rgba(6, 182, 212, 0.1) 26%, transparent 27%, transparent 74%, rgba(6, 182, 212, 0.1) 75%, rgba(6, 182, 212, 0.1) 76%, transparent 77%, transparent)`,
                          backgroundSize: '20px 20px',
                          transform: 'perspective(200px) rotateX(60deg)'
                        }}></div>
                      </div>
                    </div>

                    {/* Status Indicators */}
                    <div className="flex justify-center space-x-6 mt-12">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-gray-800 border-2 border-green-400 rounded-lg flex items-center justify-center shadow-lg shadow-green-400/50">
                          <i className="ri-check-line text-2xl text-green-400"></i>
                        </div>
                        <span className="text-xs text-green-400 font-mono">ONLINE</span>
                      </div>
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-gray-800 border-2 border-cyan-400 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-400/50">
                          <i className="ri-signal-tower-line text-2xl text-cyan-400 animate-pulse"></i>
                        </div>
                        <span className="text-xs text-cyan-400 font-mono">ACTIVE</span>
                      </div>
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-gray-800 border-2 border-orange-400 rounded-lg flex items-center justify-center shadow-lg shadow-orange-400/50">
                          <i className="ri-rocket-line text-2xl text-orange-400"></i>
                        </div>
                        <span className="text-xs text-orange-400 font-mono">READY</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media Quick Links */}
              <div className="flex justify-center space-x-6 mt-12">
                <a 
                  href="https://x.com/Adi_03_02"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-cyan-400 rounded-2xl hover:border-cyan-300 transition-all hover:scale-110 hover:shadow-2xl hover:shadow-cyan-400/50 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-cyan-400/0 group-hover:from-cyan-400/20 group-hover:to-transparent transition-all"></div>
                    <i className="ri-twitter-x-line text-4xl text-cyan-400 group-hover:text-white transition-colors relative z-10"></i>
                  </div>
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-cyan-400 font-mono text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    @Adi_03_02
                  </div>
                </a>
                <a 
                  href="https://www.linkedin.com/in/adityaprakash0302/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-cyan-400 rounded-2xl hover:border-cyan-300 transition-all hover:scale-110 hover:shadow-2xl hover:shadow-cyan-400/50 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-cyan-400/0 group-hover:from-cyan-400/20 group-hover:to-transparent transition-all"></div>
                    <i className="ri-linkedin-line text-4xl text-cyan-400 group-hover:text-white transition-colors relative z-10"></i>
                  </div>
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-cyan-400 font-mono text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    /adityaprakash0302
                  </div>
                </a>
                <a 
                  href="https://github.com/AdityaPrakash-03"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-cyan-400 rounded-2xl hover:border-cyan-300 transition-all hover:scale-110 hover:shadow-2xl hover:shadow-cyan-400/50 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-cyan-400/0 group-hover:from-cyan-400/20 group-hover:to-transparent transition-all"></div>
                    <i className="ri-github-line text-4xl text-cyan-400 group-hover:text-white transition-colors relative z-10"></i>
                  </div>
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-cyan-400 font-mono text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    AdityaPrakash-03
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center">
          <div className="text-cyan-400 animate-bounce">
            <i className="ri-arrow-down-line text-2xl"></i>
          </div>
          <div className="text-xs text-gray-400 font-mono mt-2">SCROLL TO EXPLORE</div>
        </div>
      </div>
    </section>
  );
}
